import React from 'react';
import { NavLink } from 'react-router-dom';
import { cleanseInput, cleanseDecimal } from 'src/ui/util';

import { Card, CardContent, Columns, Column, MuiTitle } from 'src/ui/mui-ui-kit';;
import propTypes from 'prop-types';
import { connect } from 'react-redux';
import { Form, Field } from 'react-final-form';
import classNames from 'classnames';
import { toast } from 'react-toastify';
import {
  setBreadcrumbs,
  UiInputAdapter,
  InnerLoader,
  SaveChangesToast,
  ConfirmChangesModal,
  reduceServerErrors,
  UiToastAdapter,
  UiFieldset,
  UiDatePickerAdapter,
} from 'src/ui';
import { getUser } from 'src/user';
import dayjs from 'dayjs-ext';
import {
  getAccount,
  fetchAccount,
  addAccountObligation,
  updateAccountObligation,
} from 'src/account';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { composeValidators, required } from 'src/ui/validations';
import { DEFAULT_DATE_FORMAT } from 'src/ui/const';

const ObligationsEditPage = ({
  fetchAccount,
  setBreadcrumbs,
  match,
  user,
  updateAccountObligation,
  addAccountObligation,
  account,
  isExpanded,

  // addObligation,
}) => {
  const [loading, setLoading] = React.useState(true);
  const [title, setTitle] = React.useState('');
  const [initialValues, setInitialValues] = React.useState({});
  const [confirmChangesModal, setConfirmChangesModal] = React.useState(false);
  const { id } = match.params;

  const buildData = id => {
    // +id returns numeric expression of id in javascript
    const obligation = account.obligations.find(element => element.id === +id);
    if (obligation) {
      const initialValues = {
        ...obligation,
        obligationDateOpened: obligation.obligationDateOpened
          ? dayjs(obligation.obligationDateOpened)
              .utc()
              .toDate()
          : null,
      };

      setInitialValues(initialValues);
    }
  };
  React.useEffect(() => {
    (async () => {
      const title = id ? 'Edit' : 'Add';

      setTitle(title);

      setBreadcrumbs([
        { label: 'Obligations', href: '/obligations' },
        { label: title, href: match.url },
      ]);

      const fiId = user.financialInstitutionId || user.fiSelectedAccountId;

      if (fiId) {
        await Promise.all([fetchAccount(user.accountId)]);

        if (id) {
          buildData(+id);
        }
      }

      setLoading(false);
    })();
  }, []);

  const toggleModal = value => {
    setConfirmChangesModal(value);
  };

  const showModal = () => {
    toggleModal(true);
  };

  const hideModal = () => {
    toggleModal(false);
  };
  const addObligation = async formValues => {
    const { ...values } = formValues;

    await addAccountObligation({
      ...values,
      accountId: user.accountId,
      obligationCreditor: values.obligationCreditor,
      obligationDateOpened: values.obligationDateOpened
        ? dayjs(values.obligationDateOpened).format(DEFAULT_DATE_FORMAT)
        : undefined,
      obligationOriginalAmount: values.obligationOriginalAmount,
      obligationMonthlyPayment: values.obligationMonthlyPayment,
      obligationSecuredBy: values.obligationSecuredBy,
      obligationPresentBalance: values.obligationPresentBalance,
    });

    if (account.isError()) {
      return reduceServerErrors(account);
    }

    toast(UiToastAdapter('Obligation added', 'success'));

    setInitialValues(values);

    return undefined;
  };

  const updateObligation = async (id, formValues) => {
    const { ...values } = formValues;

    await updateAccountObligation({
      ...values,
      accountId: user.accountId,
      obligationId: id,
      obligationCreditor: values.obligationCreditor,
      obligationDateOpened: values.obligationDateOpened
        ? dayjs(values.obligationDateOpened).format(DEFAULT_DATE_FORMAT)
        : null,
      obligationOriginalAmount: values.obligationOriginalAmount,
      obligationMonthlyPayment: values.obligationMonthlyPayment,
      obligationSecuredBy: values.obligationSecuredBy,
      obligationPresentBalance: values.obligationPresentBalance,
    });

    if (account.isError()) {
      return reduceServerErrors(account);
    }

    toast(UiToastAdapter('Obligation updated', 'success'));
    setInitialValues(values);

    return undefined;
  };

  const handleSubmit = async values => {
    if (id) {
      return updateObligation(id, values);
    }
    return addObligation(values);
  };

  const toastContainerClassName = classNames('bottom-toast-container', {
    'is-condensed': !isExpanded,
  });

  return (
    <>
      <MuiTitle isSize="4" hasTextColor="info" tag="h2">
        {`${title} Obligation`}
        <NavLink
          className="close-page button is-small is-text is-pulled-right"
          to="/obligations"
        >
          <span className="icon has-text-danger">
            <FontAwesomeIcon icon={['fal', 'times-circle']} size="2x" />
          </span>
        </NavLink>
      </MuiTitle>
      <Form
        onSubmit={handleSubmit}
        subscription={{
          submitting: true,
          dirty: true,
          hasValidationErrors: true,
          dirtySinceLastSubmit: true,
        }}
        initialValues={initialValues}
        mutators={{
          cleanse: ([event, cleansing], state, { changeValue }) => {
            cleanseInput(event, state, changeValue, cleansing);
          },
        }}
        render={({
          handleSubmit,
          submitting,
          hasValidationErrors,
          dirty,
          dirtySinceLastSubmit,
          form,
        }) => (
          <Card tag="form" noValidate onSubmit={handleSubmit}>
            <CardContent>
              <div className={toastContainerClassName}>
                <SaveChangesToast
                  message="Click the button to the right to save your changes"
                  isLoading={submitting}
                  isDisabled={submitting || hasValidationErrors}
                  isVisible={dirty || dirtySinceLastSubmit}
                  onClickButton={showModal}
                />
              </div>
              <ConfirmChangesModal
                title="Obligation"
                isActive={confirmChangesModal}
                onClickCancel={hideModal}
                onClickSubmit={hideModal}
              />
              <InnerLoader visible={submitting || loading} />
              <UiFieldset title="Financials" isFirst>
                <Columns isMultiline>
                  <Column isSize="1/2" className="is-one-third-fullhd">
                    <Field
                      component={UiInputAdapter}
                      name="obligationCreditor"
                      id="obligationCreditor"
                      required
                      label="Creditor"
                      validate={composeValidators('Creditor', required)}
                    />
                  </Column>
                  <Column isSize="1/2" className="is-one-third-fullhd">
                    <Field
                      component={UiDatePickerAdapter}
                      name="obligationDateOpened"
                      id="obligationDateOpened"
                      label="Date Opened"
                      disabled={submitting}
                      required
                      maxDate={dayjs()
                        .startOf('day')
                        .toDate()}
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      hasIcons="right"
                      renderIcons={() => [
                        <span className="icon is-right" key="right-icon">
                          <FontAwesomeIcon icon={['fal', 'calendar-alt']} />
                        </span>,
                      ]}
                      validate={composeValidators('Date Opened', required)}
                    />
                  </Column>

                  <Column isSize="1/2" className="is-one-third-fullhd">
                    <Field
                      component={UiInputAdapter}
                      name="obligationOriginalAmount"
                      id="obligationOriginalAmount"
                      required
                      label="Original Amount $"
                      validate={composeValidators(
                        'Original Amount $',
                        required
                      )}
                      onChange={e => form.mutators.cleanse(e, cleanseDecimal)}
                    />
                  </Column>

                  <Column isSize="1/2" className="is-one-third-fullhd">
                    <Field
                      component={UiInputAdapter}
                      name="obligationMonthlyPayment"
                      id="obligationMonthlyPayment"
                      required
                      label="Monthly Payment $"
                      validate={composeValidators(
                        'Monthly Payment $',
                        required
                      )}
                      onChange={e => form.mutators.cleanse(e, cleanseDecimal)}
                    />
                  </Column>
                  <Column isSize="1/2" className="is-one-third-fullhd">
                    <Field
                      component={UiInputAdapter}
                      name="obligationSecuredBy"
                      id="obligationSecuredBy"
                      required
                      label="Secured By"
                      validate={composeValidators('Secured By', required)}
                    />
                  </Column>
                  <Column isSize="1/2" className="is-one-third-fullhd">
                    <Field
                      component={UiInputAdapter}
                      name="obligationPresentBalance"
                      id="obligationPresentBalance"
                      required
                      label="Present Balance $"
                      validate={composeValidators(
                        'Present Balance $',
                        required
                      )}
                      onChange={e => form.mutators.cleanse(e, cleanseDecimal)}
                    />
                  </Column>
                </Columns>
              </UiFieldset>
            </CardContent>
          </Card>
        )}
      />
    </>
  );
};

ObligationsEditPage.propTypes = {
  match: propTypes.object.isRequired,
  user: propTypes.object.isRequired,
  account: propTypes.object.isRequired,
  setBreadcrumbs: propTypes.func.isRequired,
  fetchAccount: propTypes.func.isRequired,
  addAccountObligation: propTypes.func.isRequired,
  updateAccountObligation: propTypes.func.isRequired,
  isExpanded: propTypes.bool.isRequired,
};

function mapStateToProps(state) {
  return {
    user: getUser(state),
    account: getAccount(state),
  };
}

const connectedObligationsEditPage = connect(mapStateToProps, {
  setBreadcrumbs,
  fetchAccount,
  addAccountObligation,
  updateAccountObligation,
})(ObligationsEditPage);

export { connectedObligationsEditPage as ObligationsEditPage };
